import { z } from 'zod';
import { optionalNumber } from './OptionalNumber';
import { optionalString } from './OptionalString';
import { owner } from './Owner';
import { standardSelectionSchema } from './Selection';
import { optionalDate, scheduleSchema } from './Scheduling';
import { transformUndefinedToNull } from './Helpers';
import { relationshipSchema, RelationshipType, RelationshipTypes } from './Relationship';

const activityMethodNames = [
  'Emails',
  'Call',
  'Meeting',
  'Other',
] as const;

export const ActivityMethodNameValues = z.enum(activityMethodNames);

export type ActivityMethodName = z.infer<typeof ActivityMethodNameValues>;
export type ActivityTypeName = 'Task' | 'Reminder' | 'Note';

const systemFields = z.object({
  id: optionalNumber(),
  createdAt: optionalDate,
  updatedAt: optionalDate,
});

const userFields = z.object({
  owner: owner.optional().nullable().default(() => undefined),
  createdBy: owner.optional().nullable().default(() => undefined),
  updatedBy: owner.optional().nullable().default(() => undefined),
});

export const activityNoteSchema = z.object({
  note: z.string(),
  // TODO this should be required, waiting for BE to add
  templateJson: optionalString,
})
  .merge(systemFields)
  .merge(userFields)
  .omit({ owner: true });

export const activityReminderSchema = z.object({
  type: standardSelectionSchema.nullable().optional(),
  minutesBefore: optionalNumber(true),
  fireAt: optionalString,
  viewedAt: optionalString,
  // This is a FE only field
  checked: z.boolean().optional(),
})
  .merge(systemFields)
  .merge(userFields)
  .omit({ owner: true });

export const activityFileThumbnailSchema = z.object({
  id: z.number(),
  size: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const activityFileSchema = z.object({
  displayName: optionalString,
  location: optionalString,
  tempKey: optionalString,
  thumbnails: z.array(activityFileThumbnailSchema).optional(),
})
  .merge(systemFields)
  .merge(userFields)
  .omit({
    createdBy: true,
    updatedBy: true,
  });


const defaultNote = { note: undefined, templateJson: undefined };

const activityRelationshipSchema = relationshipSchema.extend({
  isParent: z.boolean().optional(),
});

export type ActivityRelationship = z.infer<typeof activityRelationshipSchema>;

const activityRelationships = z.array(
  activityRelationshipSchema,
)
  .optional()
  .default(() => []);

export const activitySchema = z.object({
  accounts: activityRelationships,
  campaigns: activityRelationships,
  completedAt: optionalDate,
  contacts: activityRelationships,
  endDateTime: optionalDate,
  files: z.array(activityFileSchema).optional().default(() => []),
  method: standardSelectionSchema.nullable().optional(),
  notes: z.array(activityNoteSchema).optional().default(() => [{ ...defaultNote }]),
  purpose: standardSelectionSchema.nullable().optional(),
  reminders: z.array(activityReminderSchema).optional().default(() => []),
  schedules: z.array(scheduleSchema).optional().default(() => []),
  startDateTime: optionalDate,
  title: optionalString,
  type: standardSelectionSchema,
})
  .merge(systemFields)
  .merge(userFields);


export type ActivityNote = z.infer<typeof activityNoteSchema>;
export type ActivityReminder = z.infer<typeof activityReminderSchema>;
export type ActivityFile = z.infer<typeof activityFileSchema>;
export type Activity = z.infer<typeof activitySchema>;

export type ActivityApiWrapper = {
  data: {
    activities: Activity[],
  },
  meta?: {
    nextCursor: string | null,
  }
}

export type CreateOrUpdateActivityResponse = {
  data: {
    activity: Activity;
  }
}

export const activityFrontendSchema = activitySchema.extend({
  pendingRelationships: z.array(z.object({
    key: z.string().default(() => crypto.randomUUID()),
    object: activityRelationshipSchema,
    section: RelationshipTypes,
  }))
    .optional()
    .default(() => []),
  newFiles: z.array(z.instanceof(File)).optional().default(() => []),
  rebuildFuture: z.boolean().optional(),
});

export type ActivityModel = z.infer<typeof activityFrontendSchema>;

export type PendingRelationship = {
  key: string;
  object: z.infer<typeof relationshipSchema>;
  section: RelationshipType;
}

const movePendingRelationshipsIntoArrays = (model: ActivityModel) => {
  if (!Array.isArray(model.pendingRelationships)) {
    return model;
  }

  model.pendingRelationships.forEach(relationship => {
    if (relationship.section === 'Account') {
      model.accounts.push({ ...relationship.object });
    }

    if (relationship.section === 'Contact') {
      model.contacts.push({ ...relationship.object });
    }

    if (relationship.section === 'Lead' || relationship.section === 'Opportunity') {
      model.campaigns.push({ ...relationship.object });
    }
  });

  delete model.pendingRelationships;

  return model;
};

const setAsCompletedIfNoStartDate = (model: ActivityModel) => {
  if (!model.id && !model.startDateTime && !model.completedAt) {
    model.completedAt = new Date().toISOString();
  }

  return model;
};

const transformModelToMultipartFormData = (model: ActivityModel) => {
  const fd = new FormData();

  if (!model.id && model.type?.name === 'Reminder') {
    const allRelationships: ActivityRelationship[] = [];
    if (Array.isArray(model.accounts) && model.accounts.length) {
      allRelationships.push(...model.accounts);
    }
    if (Array.isArray(model.campaigns) && model.campaigns.length) {
      allRelationships.push(...model.campaigns);
    }
    if (Array.isArray(model.contacts) && model.contacts.length) {
      allRelationships.push(...model.contacts);
    }

    const primary = allRelationships.find(relationship => relationship.isParent);
    if (primary) {
      model.title = `Reminder - ${primary.name}`;
    }
  }

  model.newFiles?.forEach(file => {
    const tempKey = crypto.randomUUID();

    model.files.push({
      displayName: file.name,
      tempKey,
    });

    fd.append(tempKey, file);
  });

  const activityData = JSON.parse(JSON.stringify(model)) as Omit<ActivityModel, 'pendingRelationships'>;
  delete activityData.newFiles;

  fd.append('activity', JSON.stringify(activityData));

  return fd;
};

export const hydrateActivityModel = activitySchema.transform(activity => {
  if (!activity?.notes?.length) {
    activity.notes = [{ ...defaultNote }];
  }

  activity?.accounts?.forEach(account => account.type = 'Account');
  activity?.contacts?.forEach(contact => contact.type = 'Contact');

  return activity;
});

const commonRequestBodyExtensions = z.object({
  notes: z.array(activityNoteSchema.partial())
    .optional()
    .transform(notes => notes?.filter(note => note.note)),
  method: standardSelectionSchema
    .optional()
    .nullable()
    .transform(method => method?.id ? method : undefined),
  purpose: standardSelectionSchema
    .optional()
    .nullable()
    .transform(purpose => purpose?.id ? purpose : undefined),
});

export const modelToRequestBody = activityFrontendSchema
  .merge(commonRequestBodyExtensions)
  .transform(transformUndefinedToNull)
  .transform(setAsCompletedIfNoStartDate)
  .transform(movePendingRelationshipsIntoArrays)
  .transform(transformModelToMultipartFormData);

export const editedActivityToRequestBody = activityFrontendSchema
  .partial()
  .merge(commonRequestBodyExtensions)
  .extend({
    id: z.number(),
  })
  .transform(transformUndefinedToNull)
  .transform(movePendingRelationshipsIntoArrays)
  .transform(transformModelToMultipartFormData);

export const populateFeFieldsOnActivityResponse = activitySchema.transform(activity => {
  activity.accounts?.forEach(account => account.type = 'Account');
  // The BE sets the type to Lead | Opportunity for activity.campaigns, so no need to do anything here
  activity.contacts?.forEach(contact => contact.type = 'Contact');

  return activity;
});

export const activityFilter = z.object({
  pageSize: optionalNumber(),
  title: optionalString,
  nextCursor: optionalString,
  type: z.array(z.number()).optional().default(() => []),
  purpose: z.array(z.number()).optional().default(() => []),
  method: z.array(z.number()).optional().default(() => []),
  users: z.array(z.number()).optional().default(() => []),
  includeCompleted: z.boolean().optional(),
  includeIncomplete: z.boolean().optional(),
  includeAllVisibleUsers: z.boolean().optional().default(true),
  minActivityDate: optionalString,
  maxActivityDate: optionalString,
  minReminderTime: optionalString,
  maxReminderTime: optionalString,
});

export type ActivityFilter = z.infer<typeof activityFilter>;

export const savableActivityFilter = activityFilter.extend({
  id: optionalNumber(),
  badgeSeverity: z.union([z.literal('danger'), z.literal('success'), z.literal('primary')]),
  collapsedByDefault: z.boolean().optional(),
  immediateLoad: z.boolean().optional().default(true),
  key: z.string(),
  dayRange: z.array(z.number()).min(2).max(2).optional().default(() => []),
  filterName: z.string(),
  sortOrder: z.number(),
  excludeFromOtherSections: z.array(z.string()).optional().default(() => []),
  overrides: optionalNumber(),
});

export type SavedActivityFilter = z.infer<typeof savableActivityFilter>;

export const activityFilterSection = savableActivityFilter.extend({
  isLoading: z.boolean().default(true),
  activities: z.array(activityFrontendSchema).default(() => []),
  shouldReload: z.boolean().optional(),
  isVisible: z.boolean().optional().default(true),
});

export type ActivityFilterSection = z.infer<typeof activityFilterSection>;

export type GetActivitiesFilterPayload = { filter: ActivityFilter };

export type ActivityFilterResponse = {
  activities: Activity[],
  meta: {
    nextCursor?: string,
  }
};

export type SavedFilterResponse = {
  data: {
    filters: Array<{
      id: number,
      userId?: number,
      filter: SavedActivityFilter,
    }>
  }
}

export enum ReminderTypes {
  Dashboard = 'ApxDashboard',
  Toast = 'ApxToast',
}
